import { useState } from 'react';
import { Inventory2Outlined, Search } from '@mui/icons-material';
import { DialogContent, DialogTitle } from '@mui/material';
import { useProductImage } from './productApi';
import {
  EmptyState,
  ModelDialog,
  ModelName,
  ModelRow,
  ModelThumb,
  NavButton,
  PickerSearch,
  SapTag,
} from './styles';
import { BasketDTO, BasketProductDTO } from './types';

/** Busca sem acento e sem caixa: "oxigenio" encontra "Oxigênio". */
const normalize = (text: string) =>
  text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const matches = (query: string, item: BasketProductDTO) => {
  const term = normalize(query.trim());
  return (
    !term ||
    normalize(item.description).includes(term) ||
    item.sap_code.includes(term)
  );
};

const ModelOption = ({
  item,
  selected,
  onSelect,
}: {
  item: BasketProductDTO;
  selected: boolean;
  onSelect: () => void;
}) => {
  const src = useProductImage(item.sap_code);
  return (
    <ModelRow>
      <ModelThumb aria-hidden="true">
        {src ? <img src={src} alt="" /> : <Inventory2Outlined />}
      </ModelThumb>
      <div>
        <ModelName>
          <h4>{item.description}</h4>
          <SapTag>{item.sap_code}</SapTag>
        </ModelName>
      </div>
      <NavButton primary={selected} onClick={onSelect}>
        {selected ? 'Selecionado' : 'Selecionar'}
      </NavButton>
    </ModelRow>
  );
};

/** Conteúdo do pop-up. Só existe enquanto ele está aberto, então a busca zera a cada abertura. */
const PickerBody = ({
  basket,
  selectedId,
  onSelect,
}: {
  basket: BasketDTO;
  selectedId?: number;
  onSelect: (productId: number) => void;
}) => {
  const [query, setQuery] = useState('');
  const list = basket.products.filter((item) => matches(query, item));

  return (
    <>
      <PickerSearch
        autoFocus
        placeholder="Buscar por nome ou código"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        startAdornment={<Search />}
        inputProps={{ 'aria-label': 'Buscar modelo' }}
        sx={{ mb: 1 }}
      />
      {list.map((item) => (
        <ModelOption
          key={item.id}
          item={item}
          selected={item.id === selectedId}
          onSelect={() => onSelect(item.id)}
        />
      ))}
      {list.length === 0 && <EmptyState>Nenhum modelo encontrado.</EmptyState>}
    </>
  );
};

/**
 * Pop-up de escolha do modelo, aberto ao clicar numa alternativa que pede um
 * (ex.: "Sim" em "Deseja incluir cilindro backup?"). Fechar sem escolher não
 * grava nada: a resposta só vale com um modelo.
 */
export const ModelPicker = ({
  basket,
  title,
  selectedId,
  isMobile,
  onSelect,
  onClose,
}: {
  /** Aberto enquanto houver balaio. */
  basket: BasketDTO | undefined;
  title: string;
  selectedId?: number;
  isMobile: boolean;
  onSelect: (productId: number) => void;
  onClose: () => void;
}) => (
  <ModelDialog
    open={Boolean(basket)}
    fullScreen={isMobile}
    onClose={onClose}
    aria-labelledby="model-picker-title"
    sx={{ '& .MuiPaper-root': { maxWidth: '620px' } }}
  >
    <DialogTitle id="model-picker-title">{title}</DialogTitle>
    <DialogContent dividers>
      {basket && (
        <PickerBody
          basket={basket}
          selectedId={selectedId}
          onSelect={onSelect}
        />
      )}
    </DialogContent>
  </ModelDialog>
);
